import moment from 'moment';
import "moment-timezone";
import _ from 'lodash/lodash';
import { formatPhoneNumber } from './phoneFormat';
import { getRestaurantTaxDisplay, getDinerTax } from './taxHelpers';
import { getCorpNameFromPayments } from './taxExemptHelpers';


/** Convenience function for going deep into a nested object and getting a value, or returning a default_config_data():
@param {Object} - Object to search
@param {String} - period delimited string of path to search e.g. 'order.delivery.grubhubDelivery'
@param {Any} - default value to return if that path doesn't exist or is null.
**/
function deepFind(obj, path, defaultValue) {
  let val = obj;
  try {
    path.split('.').forEach(partial => {
      val = val[partial];
    });
    return (typeof val === 'undefined' || val === null) ? defaultValue: val;
  } catch (e) {
    return defaultValue;
  }
}

class HomeController {
  constructor() {
    this.name = 'home';
  }

  /** Setup several different handy variables for use in the template
  **/
  $onInit() {
    this.order = deepFind(this, "confirmTemplateModel.order", {});
    this.merchant = deepFind(this, "confirmTemplateModel.merchant");
    this.merchantPhoneNumber = deepFind(this, "confirmTemplateModel.merchantPhoneNumber", {});
    this.useDmpFields = deepFind(this, "confirmTemplateModel.useDmpFields", false);
    this.view = deepFind(this, "confirmTemplateModel.view", {});
    this.useContactlessField = deepFind(this, "confirmTemplateModel.useContactlessField", false);
    this.isContactlessDelivery =  deepFind(this, "confirmTemplateModel.isContactless", false);
    this.contactlessContactMethod = deepFind(this, "confirmTemplateModel.contactlessContactMethod", null);
    this.contactlessHandoffLocation = deepFind(this, "confirmTemplateModel.contactlessHandoffLocation", null);
    this.timeZone = this.order.merchantTimezoneId;
    moment.tz.setDefault(this.timeZone);
    // originalPlacedAt is a new field, we default to placedAt if it doesn't exist
    this.originalPlacedAt = moment(this.order.originalPlacedAt || this.order.placedAt).format("MMMM DD, YYYY, h:mma");

    const charges = this.order.charges;
    this.total = this.getRestaurantGrandTotal();
    this.tax = getRestaurantTaxDisplay({
      chargesContainer: charges.exemptedTaxes || charges.taxes,
      useDmpFields: this.useDmpFields
    });

    this.deliveryFeeTax = this.getDeliveryFeeTax(charges.exemptedTaxes || charges.taxes);

    this.dinerList = this.order.diners;

    this.serviceFee = this.getServiceFee();
    this.restaurantAttributedFees = this.getRestaurantAttributedFees(this.order.charges.fees);

    const payments = this.order.payments;
    this.adjustments = this.order.payments.adjustments;
    this.isAdjusted = this.adjustments.length > 0;
    let total = this.useDmpFields ? charges.merchantTotal : charges.total;
    this.adjustmentTotal = (this.isAdjusted) ? (payments.adjustedTotal - total) : 0;
    this.restaurantAdjustedTotal = this.getRestaurantAdjustedTotal();
    this.listOfAdjustments =  (this.isAdjusted) ? this.getAdjustments() : [];
    this.corpName = getCorpNameFromPayments(payments.payments);

    this.isPayCash = _.filter(payments.payments, payment => payment.entitlementType === 'CASH').length > 0;
    this.isCatering= this.order.catering != null;
    this.isGroup = this.order.orderType == "GROUP";
    this.isDelivery = this.order.delivery != null;
    this.includeUtensils = this.order.diningSupplies == "INCLUDE";
    this.grubhubDelivery = this.isDelivery && _.get(this.order, 'delivery.grubhubDelivery');
    this.selfDelivery = this.isDelivery && !this.grubhubDelivery;
    this.effectiveTotal = (this.isAdjusted) ? payments.adjustedTotal : this.total;
    this.friendlyStatus = this.getHumanFriendlyStatus();
    this.careNumber = '(877) 585-1085';
    this.corporateCareNumber = '(844) 478-2249';
    this.restaurantCareNumber = '(800) 905-9322';
    this.merchantFulfillmentTime = this.order.merchantFulfillmentTime;
    this.restaurantFulfillmentTime = this.getRestaurantFulfillmentTime();
    this.cancelledTime = moment(this.merchantFulfillmentTime).format("MMM DD, h:mma");
    this.deliverBy = moment(this.merchantFulfillmentTime).format("MMMM DD, YYYY, h:mma");
    this.orderNumber = this.getOrderNumber(this.order.orderNumber);
    this.formattedLineGroups = this.formatLineGroups();
    this.shouldDisplayMinAdvOnLines = this.isCatering && !this.decideShowMinAdvNotice();
    this.defaultMinAdvNotice = this.generateDefaultMinAdvNotice();
    this.shouldDisplayMinAdvBox = !this.shouldDisplayMinAdvOnLines && this.isCatering && this.defaultMinAdvNotice != '';
    this.previousAmount = 0;
    this.cateringInfo = this.order.catering;
    this.hasCateringSetupInstruction = this.isCatering && this.cateringInfo.setupInstructions != null;
    this.hasAlternateContactName = this.isCatering && this.order.catering.alternateName != null && this.order.catering.alternateName != "";
    this.hasAlternateContactPhone = this.isCatering && this.order.catering.alternatePhone != null && this.order.catering.alternatePhone != "";
    this.hasAlternateContact = this.hasAlternateContactName || this.hasAlternateContactPhone;
    this.specialInstructionsTag = this.getSpecialInstructionsTag();
    this.updatedAt = moment(this.order.updatedAt).format("MMM DD, h:mma");

    this.dinersInfo = this.getDiners();
    this.isCancelledOrder = (this.getFailureStatusUpdate() != null) || this.friendlyStatus == 'cancelled';
    this.itemCountMessage = this.getItemCountMessage();

    this.curbsideInfo = this.getCurbsideInfo();
    this.isOrderTaxExemptEligible = Boolean(this.order.charges.exemptedTaxes);

    this.deliveryType = this.order.delivery ? this.order.delivery.deliveryType : null;
    if (this.deliveryType) {
      switch (this.deliveryType) {
        case "SELF":
          this.selfDelivery = true;
          this.grubhubDelivery = false;
          break;
        case "GRUBHUB":
          this.selfDelivery = false;
          this.grubhubDelivery = true;
          break;
        case "SUPPLEMENTAL_DELIVERY_WITH_GHD_AS_PICKUP":
          this.selfDelivery = false;
          this.grubhubDelivery = false;
          this.isDelivery = false;
          break;
        case "SUPPLEMENTAL_DELIVERY_AS_GHD":
          this.selfDelivery = false;
          this.grubhubDelivery = true;
          break;
        default:
          // fallback to existing logic from above
      }
    }
      this.deliveryOrPickupMsg = this.getDeliveryOrPickupMsg();
      this.serviceType = this.getServiceType();
  }

  isCurbsideFromDiner() {
    return this.order.handoffOptions ? this.order.handoffOptions.includes('CURBSIDE_PICKUP') : false;
  }

  isCurbsideFromGHD() {
    return this.order.driverHandoffOptions;
  }

  isCurbsidePickup() {
    return this.isCurbsideFromDiner() || this.isCurbsideFromGHD();
  }

  getCurbsidePhoneNumber() {
    if (this.order.driverHandoffOptions) {
      return this.order.driverHandoffOptions.contactPhone;
    } else if (this.order.merchantPickupContactPhone) {
      return this.order.merchantPickupContactPhone;
    }
    return '';
  }

  getCurbsideInfo() {
    return {
      optedIn: this.isCurbsidePickup(),
      contactNumber: this.getCurbsidePhoneNumber()
    }
  }

  _convertDollarAmount(amt) {
      return amt / 100;
  }

  getAdjustments() {
      this.previousAmount = (this.useDmpFields && this.order.charges.merchantTotal) ? this.order.charges.merchantTotal : this.order.charges.total;
      return this.adjustments.map(adjustedAmount => ({
          notes: adjustedAmount.notes,
          amount: this.getAdjustedAmounts(adjustedAmount.adjustedTotal)
        })
   );
  }

  getAdjustedAmounts(adjustedTotal) {
      var adjustmentAmount = 0;
      adjustmentAmount  = adjustmentAmount + (adjustedTotal -  this.previousAmount);
      this.previousAmount = this.previousAmount + adjustmentAmount;
      return adjustmentAmount;
  }

  decideShowMinAdvNotice() {
    var minAdvNoticeSame = true;
    const defaultMin = this.formattedLineGroups["0"].Lines["0"].LeadTimeText;
    for (let lineGroup of this.formattedLineGroups) {
      for (let line of lineGroup.Lines) {
        if (line.LeadTimeText != defaultMin) {
          minAdvNoticeSame = false;
          break;
        }
      }
    }
    return minAdvNoticeSame;
  }

  generateDefaultMinAdvNotice() {
    if (!this.shouldDisplayMinAdvOnLines && this.isCatering) {
      return this.formattedLineGroups["0"].Lines["0"].LeadTimeText
        .replace("m", " minute")
        .replace("h", " hour")
        .replace("d", " day");
    }
    return "";
  }

  formatLineGroups() {
    if (!this.order.charges.lineGroups) {
      return null;
    }
    return this.order.charges.lineGroups.map(lineGroup => ({
      Lines: lineGroup.lines.map(line => ({
        Id: line.id,
        Quantity: line.quantity || 1,
        ItemName: line.name,
        LineOptions: deepFind(line, 'lineOptions', []).map(lineOption => ({
          lineOptionName: lineOption.name})),
        Price: this._convertDollarAmount((this.useDmpFields && line.merchantTotal) ? line.merchantTotal : line.total),
        SpecialInstructions: line.specialInstructions || '',
        LeadTimeText: this.calculateLeadTimeText(line.leadTimeMs),
        MinServingSize: this.calculateMinServingSize(line.minimumServingSize, line.maximumServingSize),
        MaxServingSize: this.calculateMaxServingSize(line.minimumServingSize, line.maximumServingSize)
      })),
    }));
  }

  formatLineGroupsFromDiner(diner) {
    return {
      Lines: diner.chargesContainer.lines.lines.map(line => ({
        Id: line.id,
        Quantity: line.quantity || 1,
        ItemName: line.name,
        LineOptions: deepFind(line, 'lineOptions', []).map(lineOption => ({
          lineOptionName: lineOption.name})),
        Price: this._convertDollarAmount((this.useDmpFields && line.merchantTotal) ? line.merchantTotal : line.lineTotal),
        SpecialInstructions: line.specialInstructions || '',
        LeadTimeText: this.calculateLeadTimeText(line.leadTimeMs),
        MinServingSize: this.calculateMinServingSize(line.minimumServingSize, line.maximumServingSize),
        MaxServingSize: this.calculateMaxServingSize(line.minimumServingSize, line.maximumServingSize)
      }))
    };
  }

  calculateLeadTimeText(leadTimeMs) {
    const leadTimeSec = leadTimeMs/1000;
    const leadTimeMin = leadTimeSec/60;
    const leadTimeHour = leadTimeMin/60;
    const leadTimeDay = leadTimeHour/24;

    if(leadTimeMs == null) {
      return "";
    }
    if(leadTimeSec < 1) {
      return "";
    }
    if(leadTimeMin < 1) {
      return "";
    }
    if(leadTimeHour < 1) {
      return Math.floor(leadTimeMin) + 'm';
    }
    if(leadTimeDay < 1) {
      return Math.floor(leadTimeHour) + 'h';
    }
    return Math.floor(leadTimeDay) + 'd';
  }

  calculateMinServingSize(minServingSize, maxServingSize) {
    // this prevents the (serves 0-maxServingSize) if it is defined
    if((minServingSize == null || minServingSize == 0) && (maxServingSize != null && maxServingSize > 0)) {
      return 1;
    }
    return minServingSize == null ? 0 : minServingSize;
  }

  calculateMaxServingSize(minServingSize, maxServingSize) {
    // this prevents the (serves 40-0) if it is defined
    if((maxServingSize == null || maxServingSize == 0) && (minServingSize != null && minServingSize > 0)) {
      return minServingSize;
    }
    // if max is null and min is not, it would be caught above, so returning 0 if max is null is ok
    return maxServingSize == null ? 0 : Math.max(minServingSize, maxServingSize);
  }

  getHumanFriendlyStatus() {
    const failureStatusUpdate = this.getFailureStatusUpdate();
    if (failureStatusUpdate != null) {
      return this.isGroup ? 'groupCancelled' : failureStatusUpdate.status.toLowerCase();
    }

    if(this.isOrderScheduled()) {
      if (this.isOrderConfirmed()) {
        return this.isGroup ? 'groupConfirmed' : 'scheduledConfirmed';
      } else {
        return this.isGroup ? 'group' : 'scheduledUnconfirmed';
      }
    }
    if (this.isOrderConfirmed()) {
      return this.isGroup ? 'groupConfirmed' : 'confirmed';
    }
  }

  isOrderScheduled() {
    return this.order.fulfillmentScheduling == 'FUTURE';
  }

  isOrderConfirmed() {
    return _.some(this.order.statusHistory, statusUpdate => statusUpdate.status === 'CONFIRMED');
  }

  getFailureStatusUpdate() {
    return _.find(this.order.statusHistory, statusUpdate => statusUpdate.status === 'CANCELLED');
  }

  getServiceType() {
    this.orderForToday = moment().isSame(moment(this.merchantFulfillmentTime), 'day');
    // group orders are only self delivery for now
    if (this.isGroup) {
      return "GROUP DELIVERY";
    }
    if (this.isCatering) {
      if (this.orderForToday) {
        return "CATERING";
      } else {
        return "CATERING FOR " + moment(this.merchantFulfillmentTime).format("ddd");
      }
    } else if (this.grubhubDelivery) {
      if (this.orderForToday) {
        return "GRUBHUB DELIVERY";
      } else {
        return "GRUBHUB DELIVER ON " + moment(this.merchantFulfillmentTime).format("ddd");
      }
    } else if (this.selfDelivery) {
      if (this.orderForToday) {
        return "SELF DELIVERY";
      } else {
        return "SELF DELIVER ON " + moment(this.merchantFulfillmentTime).format("ddd");
      }
    } else {
      if (this.orderForToday) {
        return "PICKUP";
      } else {
        return "PICKUP ON " + moment(this.merchantFulfillmentTime).format("ddd");
      }
    }
  }

  getDeliveryOrPickupMsg() {
    if(this.isOrderScheduled() && !this.isGroup) {
      return this.getDeliveryOrPickupText("");
    } else {
      return this.getDeliveryOrPickupText("by");
    }
  }

  getDeliveryOrPickupText(suffix) {
    if(this.isDelivery) {
      if(this.selfDelivery) {
        return "Deliver " + suffix;
      } else {
        return "Ready for driver by";
      }
    } else {
      return "Ready for pickup " + suffix;
    }
  }

  getDiners() {
    return this.dinerList.map(diner => {
      const subtotal = this.useDmpFields ? diner.chargesContainer.merchantSubtotalBeforeCoupons : diner.chargesContainer.restaurantSubtotal
      const total = this.useDmpFields ? diner.chargesContainer.merchantTotal : diner.chargesContainer.restaurantGrandTotal
      const tax = getDinerTax({
        chargesContainer: diner.chargesContainer.exemptedTaxes || diner.chargesContainer.taxes,
        useDmpFields: this.useDmpFields
      })
      const isDinerTaxExemptEligible = Boolean(diner.chargesContainer.exemptedTaxes)
      return ({
        name: diner.dinerInfo.name,
        lineCount: this.getLineCount(diner.dinerUuid),
        total: diner.chargesContainer.restaurantGrandTotal / 100,
        participationNumber: this.getOrderNumber(diner.participationNumber),
        lineCountText: this.getLineCountText(diner.dinerUuid),
        specialInstructionText: this.getSpecialInstructionText(diner.dinerUuid),
        lines: this.formatLineGroupsFromDiner(diner),
        restaurantAttributedFees: this.getRestaurantAttributedFees(diner.chargesContainer.fees),
        isCancelled: this.isDinerCancelled(diner.orderStatus),
        isAdjusted: this.isDinerAdjusted(diner.orderStatus),
        isDinerTaxExemptEligible,
        totals: {
          subtotal: subtotal,
          total: total,
          salesTax: tax,
          tip: deepFind(diner, 'chargesContainer.tip.amount', 0)
        }
      })});
  }

  getOrderNumber(orderNumber) {
    const orderNumberLength = orderNumber.length;
    const dashPosition = orderNumberLength - 7;
    const orderNumberWithMdash = orderNumber.substring(0, dashPosition) + ' — ' + orderNumber.substring(dashPosition, orderNumberLength);
    return orderNumberLength < 7 ? orderNumber :  orderNumberWithMdash;;
  }

  getLineCount(dinerUuid) {
    return this.view.countDinerLineItems.get(dinerUuid);
  }

  getSpecialInstructionCount(dinerUuid) {
    return this.view.countDinerSpecialInstruction.get(dinerUuid);
  }

  getLineCountText(dinerUuid) {
    var lineCountNumber = this.getLineCount(dinerUuid);
    return lineCountNumber == 1 ? lineCountNumber + " item" : lineCountNumber + " items";
  }

  getSpecialInstructionText(dinerUuid) {
    var lineCountNumber = this.getLineCount(dinerUuid);
    var specialInstructionCount = this.getSpecialInstructionCount(dinerUuid);
    if (!specialInstructionCount || specialInstructionCount < 1) {
      return null;
    } else if (lineCountNumber == 1) {
      return "contains special instructions";
    } else if (lineCountNumber == specialInstructionCount) {
      return "contain special instructions";
    } else {
      return "some contain special instructions";
    }
  }

  isDinerCancelled(dinerStatus) {
    return (dinerStatus == "CANCELLED");
  }

  isDinerAdjusted(dinerStatus) {
    return dinerStatus == "ADJUSTED";
  }

  getItemCountMessage() {
    if(this.view.countOrderLineItems === 1) {
      return this.view.countOrderLineItems+" item"
    } else {
      return this.view.countOrderLineItems+" items"
    }
  }

  getSpecialInstructionsTag() {
    const itemsWithInstructionsCount = this.view.countOrderLineItemsSpecialInstructions;
    const itemsCount = this.view.countOrderLineItems;
    if (!itemsWithInstructionsCount || itemsWithInstructionsCount < 1) {
      return null;
    }
    if (itemsCount === 1) {
      return "contains special instructions"
    } else if (itemsCount === itemsWithInstructionsCount) {
      return "contain special instructions"
    } else {
      return "some contain special instructions"
    }
  }

  getRestaurantGrandTotal() {
    return (this.useDmpFields && this.order.charges.merchantTotal)? this.order.charges.merchantTotal :
        this.view.aggregatedGrandTotal ? this.view.aggregatedGrandTotal : this.order.charges.restaurantGrandTotal;
  }

  getRestaurantAdjustedTotal() {
    if (this.useDmpFields && this.order.charges.merchantTotal ) {
      return this.order.charges.merchantTotal
    } 
    if (this.view.aggregatedAdjustedTotal) {
      return this.view.aggregatedAdjustedTotal
    }
    if (this.order.charges.restaurantAdjustedTotal) {
      return this.order.charges.restaurantAdjustedTotal;
    }
    return this.order.payments.adjustedTotal;
  }

  getDeliveryFeeTax(chargesContainerPath) {
    return chargesContainerPath.delivery;
  }

  getServiceFee() {
    if (this.isGroup) return null;

    if (!this.dinerList) return 0;
    if (!this.dinerList[0].chargesContainer) return 0;
    if (!this.dinerList[0].chargesContainer.fees) return 0;
    if (!this.dinerList[0].chargesContainer.fees.fees) return 0;

    const feesArray = this.dinerList[0].chargesContainer.fees.fees;
    if (!feesArray.length) return 0;

    const serviceFeeObject = feesArray.find(fee => fee.type === 'CHAIN_SERVICE_FEE');
    if (!serviceFeeObject) return 0;

    return serviceFeeObject.amount;
  }

  showSubtotalsDividerLine(index) {
    return !(this.grubhubDelivery && (index + 1 >= this.dinersInfo.length))
  }

  formattedPhoneNumber(number) {
    return formatPhoneNumber(number);
  }

  getRestaurantAttributedFees(feesArray) {
    if (!feesArray) return [];

    if (feesArray.hasOwnProperty('fees')) feesArray = feesArray.fees;

    const restaurantAttributedFeesArray = feesArray.filter(fee => fee.attribution === 'RESTAURANT');

    if (!restaurantAttributedFeesArray) return [];

    return restaurantAttributedFeesArray;
  }

  getRestaurantFulfillmentTime() {
    const fulfillmentTime = deepFind(this, "confirmTemplateModel.estimatedFulfillmentAt", this.merchantFulfillmentTime);
    return moment(fulfillmentTime).format("MMM D, h:mm A");
  }
}

HomeController.$inject = [];

export default HomeController;



// WEBPACK FOOTER //
// ./client/app/components/home/home.controller.js